import { AxiosError, AxiosInstance, AxiosResponse, AxiosStatic } from 'axios';
import { toast } from '@britishcouncil/react-admin';

const RESPONSES_WITH_ERRORS = [400];

export interface ServerError {
  propertyName?: string;
  message: string;
}

//Logic of this interceptor was moved from angularJS application for backward compatibility
export function includeApiValidationErrorInterceptor(axios: AxiosInstance | AxiosStatic) {
  axios.interceptors.response.use(
    (resp: AxiosResponse<unknown>) => resp,
    async (error: AxiosError) => {
      handleError(error, showErrorToast);
      return Promise.reject(error);
    }
  );
}

export const handleError = (error: AxiosError, showErrorToast: (message: string) => void) => {
  if (error.response) {
    if (error.response.status === 409) {
      showErrorToast('This page has been modified. Please refresh the page to see changes.');
    } else if (
      !isResponseWithErrors(error.response) &&
      !isUnauthorizedError(error.response) &&
      !isNoPermissionToAccessError(error.response) &&
      !isCustomErrorHandling(error.response) &&
      !isAbortedConnectionError(error)
    ) {
      showErrorToast('Error occurred when processing your request');
    }
  }
};

const showErrorToast = (message: string) => {
  toast({ type: 'error', title: 'Error', message, displayTime: 5000, closeIcon: true });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getApiErrors = (response: AxiosResponse<any, unknown>): Array<ServerError> | undefined => {
  if (response && response.data) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data.errors ?? response.data.error)?.map((x: any) => ({
      propertyName: x.propertyName,
      message: x.message,
    }));
  }

  return undefined;
};

const isResponseWithErrors = (response: AxiosResponse<unknown, unknown>) => {
  if (!response || RESPONSES_WITH_ERRORS.indexOf(response.status) === -1) {
    return false;
  }

  return !!getApiErrors(response);
};

const isUnauthorizedError = (response: AxiosResponse<unknown, unknown>) =>
  !!response && response.status === 401;

const isNoPermissionToAccessError = (response: AxiosResponse<unknown, unknown>) =>
  !!response && response.status === 406;

const isCustomErrorHandling = (
  response: AxiosResponse<unknown, { customErrorHandling?: unknown }>
): boolean =>
  !!response &&
  !!response.config &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (!!(response.config as any).customErrorHandling ||
    (!!response.config.data && !!response.config.data.customErrorHandling));

const isAbortedConnectionError = (error: AxiosError): boolean => error?.code === 'ECONNABORTED';
